import { ElementData, EptForm } from "@/components/form-builder/utils/types";
import { z } from "zod";
import {
  findValidatorWithType,
  getZodValidator,
  isElementNotEmpty,
  validateCNP,
} from "@ept/shared/src/validators";
import { FieldValues } from "react-hook-form";
import { elementCreateUpdateSchema } from "@ept/shared/src/schemas";
import { ELEMENT_TYPES } from "@ept/shared";
import { RouterOutputs } from "@ept/api";
import { apiVanilla } from "@/lib/trpc/client";

let clientSideDynamicElements: {
  element: ElementData["element"];
  sectionIndex: number;
  id: string;
}[] = [];

export const getVisibleDynamicElements = (formValues: FieldValues, form?: EptForm) => {
  if (!form) {
    return null;
  }
  return clientSideDynamicElements.filter((element) => {
    const clientSideDynamicValidator = findValidatorWithType(
      element.element.validators,
      "CLIENT_SIDE_DYNAMIC",
    );
    const dependentElementId = form.sections[element.sectionIndex]?.section.elements.find(
      (el) => el.element.identifier === clientSideDynamicValidator?.validator.options?.elementId,
    )?.id;

    if (!dependentElementId) {
      return false;
    }
    if (
      formValues[dependentElementId] !==
      clientSideDynamicValidator?.validator.options?.requiredValue
    ) {
      return false;
    }

    return true;
  });
};

export const getFrontendTableValidator = (element: z.infer<typeof elementCreateUpdateSchema>) => {
  if (!element.extraAttributes) {
    return z.object({}).optional();
  }
  const validators = element.extraAttributes.validators;

  const tableValidator: Record<string, ReturnType<typeof getZodValidator>> = {};
  element.extraAttributes?.columns?.forEach((column) => {
    const columnValidators = validators?.filter((validator) => validator.columnName === column.id);

    if (!columnValidators?.length) {
      return;
    }

    tableValidator[column.id] = getZodValidator(
      columnValidators.map((validator) => ({ validator })),
      (column.type || "TEXT_BOX") as (typeof ELEMENT_TYPES)[number],
    );
  });

  return z.array(z.object(tableValidator)).optional();
};

export const getFormValidators = (
  form?: RouterOutputs["formManagementRouter"]["getFormDataBySlug"],
) => {
  const formSchema: any = {};
  if (!form) {
    return {
      resolver: z.object(formSchema),
      schema: z.object(formSchema),
    };
  }

  clientSideDynamicElements = [];
  form.sections.forEach((section, sectionIndex) =>
    section.section.elements.forEach((element) => {
      const clientSideDynamicValidator = findValidatorWithType(
        element.element.validators,
        "CLIENT_SIDE_DYNAMIC",
      );
      if (clientSideDynamicValidator) {
        clientSideDynamicElements.push({
          id: element.id!,
          element: element.element,
          sectionIndex,
        });
      }
      if (element.element.type === "TABLE") {
        const tableValidators = getFrontendTableValidator(element.element);
        formSchema[element.id!] = tableValidators;
      } else {
        const validator = getZodValidator(element.element.validators, element.element.type);

        formSchema[element.id!] = validator;
      }
    }),
  );

  return {
    resolver: z.object(formSchema).superRefine(async (value, ctx) => {
      const elements = getVisibleDynamicElements(value, form);
      for (const element of elements || []) {
        const validators = element.element.validators;
        const hasRequired = findValidatorWithType(validators, "REQUIRED");
        const val = value[element!.id!];
        const elementId = element!.id!;
        if (hasRequired && !isElementNotEmpty(val, element.element.type)) {
          ctx.addIssue({
            code: "custom",
            path: [elementId],
            message: hasRequired.validator.customErrorMessage ?? "Acest camp este obligatoriu",
          });
        }

        const hasLength = findValidatorWithType(validators, "LENGTH");
        if (hasLength && typeof val === "string") {
          if (hasLength.validator.options?.min && val.length < hasLength.validator.options.min) {
            ctx.addIssue({
              code: "custom",
              path: [elementId],
              message:
                hasLength.validator.customErrorMessage ??
                `Acest camp trebuie sa contina minim ${hasLength.validator.options.min} caractere`,
            });
          }
          if (hasLength.validator.options?.max && val.length > hasLength.validator.options.max) {
            ctx.addIssue({
              code: "custom",
              path: [elementId],
              message:
                hasLength.validator.customErrorMessage ??
                `Acest camp trebuie sa contina maxim ${hasLength.validator.options.max} caractere`,
            });
          }
        }

        const hasRegex = findValidatorWithType(validators, "REGEX");
        if (hasRegex && typeof val === "string") {
          const regex = new RegExp(hasRegex.validator.options!.value!);
          if (!regex.test(val)) {
            ctx.addIssue({
              code: "custom",
              path: [elementId],
              message: hasRegex.validator.customErrorMessage ?? `Acest câmp nu respectă formatul`,
            });
          }
        }

        const hasPersonalCode = findValidatorWithType(validators, "PERSONAL_CODE");
        if (hasPersonalCode && typeof val === "string") {
          if (!validateCNP(val)) {
            ctx.addIssue({
              code: "custom",
              path: [elementId],
              message:
                hasPersonalCode.validator.customErrorMessage ??
                `Acest câmp nu respectă formatul pentru CNP`,
            });
          }
        }

        if (findValidatorWithType(validators, "PERSONAL_CODE_UNIQUE")) {
          const isGood = await apiVanilla.formsPublicRouter.checkPersonalCodeUnique.query({
            personalCode: val,
          });
          if (!isGood) {
            ctx.addIssue({
              code: "custom",
              path: [elementId],
              message: "CNP-ul trebuie să fie unic.",
            });
          }
        }
      }
    }),
    schema: z.object(formSchema),
  };
};
